import { AuthFailureCause } from '@ninety/mobile/platform/auth';

/**
 * What to tell someone whose attempt to enroll from this sheet did not work.
 *
 * Only the causes the enrollment task can raise reach here, so anything else returns `null` rather
 * than a message written for another screen. This sheet only ever enables, which is why the copy has
 * one direction where the Settings screen's has one in the other.
 */
export function enrollmentFailureMessage(cause: AuthFailureCause | null): string | null {
  if (!cause) return null;

  switch (cause.kind) {
    // Nothing on this path prompts — enrolling is a keychain write, and `main` verifies nothing here
    // either — so none of the three prompt causes is reachable. Kept because the union carries them and
    // this switch is exhaustive by design.
    case 'biometricCancelled':
    case 'biometricRejected':
    case 'biometricLockedOut':
      return null;
    /**
     * The keychain write failed, or the held password was gone by the time this ran.
     *
     * The second is the reachable one, and it is why the copy does not promise a retry will work: the
     * window closes when the app is killed, and the honest next step is the next sign-in. `Settings`
     * cannot help — it is off-only — so it is deliberately not named.
     *
     * `unexpectedFailure` shares the copy: the handler threw past its own classifier, and nothing about
     * this sheet's one direction changed either way, which is the reassurance worth giving.
     */
    case 'vaultUnavailable':
    case 'unexpectedFailure':
      return 'We could not turn that on. Nothing has changed, and we will offer it again next time.';
    case 'sessionBusy':
      // Nothing was attempted, so this must not read as a failure to store anything.
      return 'Your session was being refreshed. Please try that again in a moment.';
    default:
      return null;
  }
}
